import { useMemo } from "react";

import { typedQueryClient } from "hooks/useTypedQuery";
import { DealerLicenseQueryKeys } from "modules/DealerLicenseOverview/queryKeys";

import type { WebSocketMessageListener } from "components";
import type { Dealer, ExactCategory, ExactStandaloneLicense } from "models";

type UseDealerLicenseOverviewListenersProps = {
  selectedDealer?: Dealer | null;
};

export const useDealerLicenseOverviewListeners = ({ selectedDealer }: UseDealerLicenseOverviewListenersProps) => {
  const listeners = useMemo(() => {
    if (!selectedDealer?.id) return [];

    const exactStandaloneLicenseListeners: WebSocketMessageListener<ExactStandaloneLicense>[] = [
      {
        model: "ExactStandaloneLicense",
        action: "upsert",
        callback: ({ data: license }) => {
          const licenses = typedQueryClient.getQueryData(DealerLicenseQueryKeys.licenses);
          if (!licenses) return;

          const existingLicense = licenses.standalone_licenses?.some(lc => lc.id === license.id);
          typedQueryClient.setQueryData(DealerLicenseQueryKeys.licenses, {
            ...licenses,
            standalone_licenses: existingLicense
              ? licenses.standalone_licenses?.map(lc => (lc.id === license.id ? license : lc))
              : [...(licenses.standalone_licenses ?? []), license]
          });
        }
      },
      {
        model: "ExactStandaloneLicense",
        action: "delete",
        callback: ({ data: license }) => {
          const licenses = typedQueryClient.getQueryData(DealerLicenseQueryKeys.licenses);
          if (!licenses) return;

          typedQueryClient.setQueryData(DealerLicenseQueryKeys.licenses, {
            ...licenses,
            standalone_licenses: licenses.standalone_licenses?.filter(lc => lc.id !== license.id)
          });
        }
      }
    ];

    const exactCategoryListeners: WebSocketMessageListener<ExactCategory>[] = [
      {
        model: "ExactCategory",
        action: "upsert",
        callback: ({ data: category }) => {
          const licenses = typedQueryClient.getQueryData(DealerLicenseQueryKeys.licenses);
          if (!licenses) return;

          const existingLicenseCategory = licenses.categories?.some(c => c.id === category.id);
          typedQueryClient.setQueryData(DealerLicenseQueryKeys.licenses, {
            ...licenses,
            categories: existingLicenseCategory ? licenses.categories?.map(c => (c.id === category.id ? category : c)) : [...(licenses.categories || []), category]
          });
        }
      }
    ];

    return [...exactStandaloneLicenseListeners, ...exactCategoryListeners];
  }, [selectedDealer?.id]);

  return listeners;
};
